import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Wallet, User, Landmark, FileText, CheckCircle, Lock, RotateCcw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { FlujoSyncBadge } from '@/components/FlujoSyncBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESTADO_META = {
  disponible: { label: 'Disponible', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60' },
  parcial: { label: 'Parcialmente aplicado', tone: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60' },
  liquidado: { label: 'Liquidado', tone: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700' },
  cerrado: { label: 'Cerrado', tone: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60' },
};

const hasPerm = (user, perm) => (user?.role === 'admin') || (user?.permissions || []).includes(perm);

export default function AdelantoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [adelanto, setAdelanto] = useState(null);
  const [loading, setLoading] = useState(true);
  const canCerrar = hasPerm(user, 'cerrar_adelanto');
  const canRevertir = hasPerm(user, 'revertir_adelanto');
  const canCreate = hasPerm(user, 'crear_adelanto');
  const [sending, setSending] = useState(false);
  const handleSendReminder = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/adelantos/${id}/send-reminder`);
      toast.success(`Recordatorio enviado a ${r.data.to}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar');
    } finally { setSending(false); }
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/adelantos/${id}`);
      setAdelanto(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el adelanto');
      navigate('/adelantos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/adelantos/${id}`);
        setAdelanto(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudo cargar el adelanto');
        navigate('/adelantos');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleClose = async () => {
    const motivo = window.prompt('Motivo del cierre (opcional):', '');
    if (motivo === null) return;
    try {
      await axios.post(`${API}/adelantos/${id}/close`, { motivo });
      toast.success('Adelanto cerrado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cerrar');
    }
  };
  const handleRevert = async (app) => {
    const motivo = window.prompt(`¿Revertir Q${Number(app.monto_aplicado).toFixed(2)}? El monto regresará al saldo del adelanto.\nMotivo (opcional):`, '');
    if (motivo === null) return;
    try {
      await axios.post(`${API}/adelantos/${id}/revert`, { payment_id: app.payment_id, applied_at: app.applied_at, motivo });
      toast.success('Aplicación revertida');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo revertir');
    }
  };
  const handleReopen = async () => {
    try {
      await axios.post(`${API}/adelantos/${id}/reopen`);
      toast.success('Adelanto reabierto');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo reabrir');
    }
  };

  if (loading || !adelanto) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"><p className="text-slate-500">Cargando...</p></div>;
  }

  const meta = ESTADO_META[adelanto.estado] || ESTADO_META.disponible;
  const createdAt = adelanto.created_at ? format(new Date(adelanto.created_at), "dd MMM yyyy 'a las' HH:mm", { locale: es }) : '';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="adelanto-detail-page">
      <div className="container mx-auto px-4 py-6 lg:py-8">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex gap-1">
            <Button variant="ghost" onClick={() => navigate('/adelantos')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver
            </Button>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              Dashboard
            </Button>
          </div>
          <div className="flex gap-2">
            {canCerrar && adelanto.estado !== 'cerrado' && adelanto.estado !== 'liquidado' && (
              <Button variant="outline" onClick={handleClose} className="border-blue-500 text-blue-700 dark:text-blue-300" data-testid="close-btn">
                <Lock className="h-4 w-4 mr-2" /> Cerrar manualmente
              </Button>
            )}
            {canCerrar && adelanto.estado === 'cerrado' && (
              <Button variant="outline" onClick={handleReopen} data-testid="reopen-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reabrir
              </Button>
            )}
          </div>
        </div>

        {/* Hero */}
        <Card className="p-6 mb-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className={meta.tone}>{meta.label}</Badge>
                {adelanto.pendiente_devolucion && (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60">
                    <AlertCircle className="h-3 w-3 mr-1 inline" /> Pendiente de devolución
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-slate-100">
                Adelanto a {adelanto.beneficiario_nombre}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Creado {createdAt} por {adelanto.created_by_name}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Saldo disponible</p>
              <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
                Q{Number(adelanto.monto_disponible).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                de Q{Number(adelanto.monto_total).toLocaleString('es-GT', { minimumFractionDigits: 2 })} entregados
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Datos */}
          <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">Datos del depósito</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <F icon={User} label="Beneficiario" value={adelanto.beneficiario_nombre} />
              <F icon={Landmark} label="Banco" value={adelanto.banco || '—'} />
              <F label="Número de cuenta" value={adelanto.numero_cuenta || '—'} />
              <F label="Titular" value={adelanto.titular_cuenta || '—'} />
              <F label="Tipo cuenta" value={adelanto.tipo_cuenta || '—'} />
              <F label="Fecha depósito" value={adelanto.fecha_deposito || '—'} />
            </dl>
            {adelanto.nota && (
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Nota</p>
                <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line">{adelanto.nota}</p>
              </div>
            )}
            {adelanto.close_reason && (
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Motivo cierre</p>
                <p className="text-sm text-slate-800 dark:text-slate-200">{adelanto.close_reason}</p>
                {adelanto.closed_by_name && (
                  <p className="text-[11px] text-slate-500 mt-1">Cerrado por {adelanto.closed_by_name}</p>
                )}
              </div>
            )}
          </Card>

          {/* Boleta */}
          <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">Boleta de transferencia</h2>
            {adelanto.boleta_file ? (
              <a href={adelanto.boleta_file.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
                <FileText className="h-4 w-4 text-[#2c57c5]" />
                <span className="text-sm text-slate-800 dark:text-slate-200">{adelanto.boleta_file.original_filename}</span>
              </a>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-500 italic">Sin boleta adjunta</p>
            )}
          </Card>
        </div>

        {/* Aplicaciones */}
        <Card className="mt-6 p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="applications-card">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">
            Aplicaciones a pagos ({(adelanto.aplicaciones || []).length})
          </h2>
          {(adelanto.aplicaciones || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aún no se ha aplicado este adelanto a ningún pago</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2 uppercase text-[10px] tracking-wider">Pago (proveedor)</th>
                    <th className="text-left font-semibold px-3 py-2 uppercase text-[10px] tracking-wider">Serie/DT</th>
                    <th className="text-right font-semibold px-3 py-2 uppercase text-[10px] tracking-wider">Monto aplicado</th>
                    <th className="text-left font-semibold px-3 py-2 uppercase text-[10px] tracking-wider">Fecha</th>
                    <th className="text-left font-semibold px-3 py-2 uppercase text-[10px] tracking-wider">Por</th>
                    {canRevertir && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {(adelanto.aplicaciones || []).map((app, idx) => {
                    const p = app.payment || {};
                    let when = '';
                    try { when = format(new Date(app.applied_at), 'dd MMM yy HH:mm', { locale: es }); } catch { /* ignore */ }
                    return (
                      <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-3 py-2">
                          {p.id ? (
                            <button className="text-[#2c57c5] hover:underline" onClick={() => navigate(`/payments/${p.id}`)}>
                              {p.proveedor_nombre || p.proveedor || p.id.slice(0, 8)}
                            </button>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{p.serie || '—'} · {p.numero_dt || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                          Q{Number(app.monto_aplicado).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-slate-500 dark:text-slate-500">{when}</td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{app.applied_by_name}</td>
                        {canRevertir && (
                          <td className="px-3 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleRevert(app)} className="text-red-600 hover:text-red-700 dark:text-red-400" data-testid={`revert-app-${idx}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revertir
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="reminder-status">
          Recordatorio por correo (5 h después):{' '}
          <span className="font-semibold">
            {adelanto.recordatorio_estado === 'enviado' ? `enviado a ${adelanto.recordatorio_detalle}`
              : adelanto.recordatorio_estado === 'omitido' ? `omitido (${adelanto.recordatorio_detalle})`
              : adelanto.recordatorio_estado === 'error' ? `error: ${adelanto.recordatorio_detalle}`
              : 'programado'}
          </span>
          {canCreate && (
            <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-[#2c57c5]" onClick={handleSendReminder} disabled={sending} data-testid="send-reminder-btn">
              {sending ? 'Enviando…' : 'Enviar recordatorio ahora'}
            </Button>
          )}
        </p>
        <div className="mt-6"><FlujoSyncBadge origenId={id} /></div>

        {(adelanto.reversiones || []).length > 0 && (
          <Card className="mt-6 p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="reversions-card">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">
              Reversiones ({adelanto.reversiones.length})
            </h2>
            <ul className="space-y-2">
              {adelanto.reversiones.map((r, idx) => {
                let when = '';
                try { when = format(new Date(r.reverted_at), 'dd MMM yy HH:mm', { locale: es }); } catch { /* ignore */ }
                return (
                  <li key={idx} className="flex items-center justify-between gap-3 text-sm border border-slate-100 dark:border-slate-800 rounded-md px-3 py-2" data-testid={`reversion-${idx}`}>
                    <div className="min-w-0">
                      <button className="text-[#2c57c5] hover:underline" onClick={() => navigate(`/payments/${r.payment_id}`)}>Pago {String(r.payment_id).slice(0, 8)}…</button>
                      <span className="text-slate-500 dark:text-slate-400"> · {when} · por {r.reverted_by_name}</span>
                      {r.motivo && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Motivo: {r.motivo}</p>}
                    </div>
                    <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums shrink-0">
                      −Q{Number(r.monto_aplicado).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

function F({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="h-7 w-7 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
          <Icon className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</dt>
        <dd className="text-sm text-slate-900 dark:text-slate-100 mt-0.5">{value}</dd>
      </div>
    </div>
  );
}
